import { apiSlice } from "../../../features/api/apiSlice";

export type CommitteeMemberRow = {
  id: string;
  committee_id: number;
  member_id: string;
  committee_role: "chairman" | "secretary" | "member";
  position: string | null;
  removed_at: string | null;
  created_at?: string;
  member?: {
    id: string;
    surname?: string;
    firstname?: string;
    othernames?: string | null;
    email?: string;
    phone?: string;
    membership_no?: string;
  };
};

export type Committee = {
  id: number;
  executive_set_id: number;
  name: string;
  description: string | null;
  chairman_executive_id: number | null;
  role_id: number | null;
  is_active: boolean;
  committee_type: "executive_set" | "open";
  members_count?: number;
  chairman?: {
    id: number;
    member_id: string;
    member?: {
      id: string;
      surname?: string;
      firstname?: string;
      othernames?: string | null;
    };
    executiveOffice?: { id: number; name: string };
  };
  members?: CommitteeMemberRow[];
};

export type MemberSearchResult = {
  id: string;
  surname?: string;
  firstname?: string;
  othernames?: string | null;
  email?: string;
  membership_no?: string;
};

function pick(res: any) {
  return res?.data ?? res;
}

export const executiveCommitteeApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCommitteesBySet: builder.query<Committee[], number>({
      query: (setId) => `/executive-set-commitee/${setId}/committees`,
      transformResponse: (res: any) => {
        const d = pick(res);
        return Array.isArray(d?.committees)
          ? d.committees
          : Array.isArray(d)
          ? d
          : [];
      },
      providesTags: (_r, _e, setId) => [
        { type: "Committees" as const, id: `SET:${setId}` },
      ],
    }),

    createSetCommittee: builder.mutation<
      Committee,
      {
        setId: number;
        name: string;
        description?: string;
        committee_type?: "executive_set" | "open";
        chairman_executive_id?: number | null;
      }
    >({
      query: ({ setId, ...body }) => ({
        url: `/executive-set-commitee/${setId}/committees`,
        method: "POST",
        body,
      }),
      transformResponse: (res: any) => pick(res)?.committee ?? pick(res),
      transformErrorResponse: (err: any) => err,
      invalidatesTags: (_r, _e, arg) => [
        { type: "Committees" as const, id: `SET:${arg.setId}` },
      ],
    }),

    updateSetCommittee: builder.mutation<
      Committee,
      {
        committeeId: number;
        setId: number;
        name?: string;
        description?: string;
        committee_type?: "executive_set" | "open";
        chairman_executive_id?: number | null;
        is_active?: boolean;
      }
    >({
      query: ({ committeeId, setId: _s, ...body }) => ({
        url: `/executive-set-commitee/${committeeId}`,
        method: "PUT",
        body,
      }),
      transformResponse: (res: any) => pick(res)?.committee ?? pick(res),
      invalidatesTags: (_r, _e, arg) => [
        { type: "Committees" as const, id: `SET:${arg.setId}` },
      ],
    }),

    deleteSetCommittee: builder.mutation<
      void,
      { committeeId: number; setId: number }
    >({
      query: ({ committeeId }) => ({
        url: `/executive-set-commitee/${committeeId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "Committees" as const, id: `SET:${arg.setId}` },
      ],
    }),

    getSetCommitteeMembers: builder.query<
      { members: CommitteeMemberRow[]; chairman: any },
      number
    >({
      query: (committeeId) => `/committees/${committeeId}/members`,
      transformResponse: (res: any) => ({
        members: res?.members ?? [],
        chairman: res?.chairman ?? null,
      }),
      providesTags: (_r, _e, id) => [{ type: "CommitteeMembers" as const, id }],
    }),

    addSetCommitteeMember: builder.mutation<
      any,
      {
        committeeId: number;
        setId: number;
        member_id: string;
        committee_role: string;
        position?: string;
      }
    >({
      query: ({ committeeId, setId: _s, ...body }) => ({
        url: `/committees/${committeeId}/members`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "CommitteeMembers" as const, id: arg.committeeId },
        { type: "Committees" as const, id: `SET:${arg.setId}` },
      ],
    }),

    updateSetCommitteeMember: builder.mutation<
      any,
      {
        committeeId: number;
        memberId: string;
        setId: number;
        committee_role?: string;
        position?: string;
      }
    >({
      query: ({ committeeId, memberId, setId: _s, ...body }) => ({
        url: `/committees/${committeeId}/members/${memberId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "CommitteeMembers" as const, id: arg.committeeId },
        { type: "Committees" as const, id: `SET:${arg.setId}` },
      ],
    }),

    removeSetCommitteeMember: builder.mutation<
      any,
      {
        committeeId: number;
        memberId: string;
        setId: number;
        /** 'drop' = soft-remove, keeps record for reference (default).
         *  'delete' = hard-delete, use when the member was added in error. */
        action?: "drop" | "delete";
      }
    >({
      query: ({ committeeId, memberId, action = "drop" }) => ({
        url: `/committees/${committeeId}/members/${memberId}?action=${action}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "CommitteeMembers" as const, id: arg.committeeId },
        { type: "Committees" as const, id: `SET:${arg.setId}` },
      ],
    }),

    searchMembersForCommittee: builder.query<MemberSearchResult[], string>({
      query: (q) => `/members/search/${encodeURIComponent(q)}`,
      transformResponse: (res: any) => {
        if (Array.isArray(res)) return res;
        if (Array.isArray(res?.data)) return res.data;
        return [];
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetCommitteesBySetQuery,
  useCreateSetCommitteeMutation,
  useUpdateSetCommitteeMutation,
  useDeleteSetCommitteeMutation,
  useGetSetCommitteeMembersQuery,
  useAddSetCommitteeMemberMutation,
  useUpdateSetCommitteeMemberMutation,
  useRemoveSetCommitteeMemberMutation,
  useLazySearchMembersForCommitteeQuery,
} = executiveCommitteeApi;
